import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  API_ROUTES,
  emptyWordleStats,
  isWordleLetter,
  normalizeWordleGuess,
  recordWordleFinish,
  wordleKeyboardColors,
  WORDLE_KEY_ROWS,
  WORDLE_MAX_GUESSES,
  WORDLE_WORD_LENGTH,
  type WordleAllowedResponse,
  type WordleColor,
  type WordleGuessResponse,
  type WordleLeaderboard,
  type WordleLocalStats,
  type WordleMode,
  type WordleAdminResetResponse,
  type WordlePublicRound,
  type WordleStateResponse,
} from "@ziolo/shared";
import { Button, Card, useFlash } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { confirmAdmin } from "../hooks/useIsAdmin.js";
import { useSession } from "../session/SessionProvider.js";
import "../styles/wordle.css";

const STATS_KEY = "ziolo.wordle.stats";
const RESET_KEY = "ziolo.wordle.reset";
const MODE_KEY = "ziolo.wordle.mode";
const LEADERBOARD_MS = 15_000;

function loadMode(): WordleMode {
  try {
    return localStorage.getItem(MODE_KEY) === "unlimited" ? "unlimited" : "daily";
  } catch {
    return "daily";
  }
}

function loadStats(): WordleLocalStats {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (!raw) return emptyWordleStats();
    const parsed = JSON.parse(raw) as Partial<WordleLocalStats>;
    const dist = parsed.guessDistribution;
    return {
      played: parsed.played ?? 0,
      won: parsed.won ?? 0,
      currentStreak: parsed.currentStreak ?? 0,
      maxStreak: parsed.maxStreak ?? 0,
      guessDistribution:
        Array.isArray(dist) && dist.length === 6
          ? [dist[0] ?? 0, dist[1] ?? 0, dist[2] ?? 0, dist[3] ?? 0, dist[4] ?? 0, dist[5] ?? 0]
          : [0, 0, 0, 0, 0, 0],
    };
  } catch {
    return emptyWordleStats();
  }
}

function saveStats(stats: WordleLocalStats) {
  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch {
    // Private mode — local stats just don't persist.
  }
}

function loadResetVersion(): number {
  try {
    const parsed = Number.parseInt(localStorage.getItem(RESET_KEY) ?? "0", 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function saveResetVersion(version: number) {
  try {
    localStorage.setItem(RESET_KEY, String(version));
  } catch {
    // ignore
  }
}

function statusText(round: WordlePublicRound | null): string {
  if (!round) return "Wczytywanie...";
  if (round.won) return "Brawo! Odgadłeś hasło.";
  if (round.finished) return `Koniec gry. Hasło: ${round.answer ?? "?"}`;
  return `Próba ${round.guesses.length + 1}/${WORDLE_MAX_GUESSES}`;
}

function emptyRound(mode: WordleMode): WordlePublicRound {
  return {
    id: "",
    mode,
    dayKey: "",
    guesses: [],
    evaluations: [],
    finished: false,
    won: false,
    maxGuesses: WORDLE_MAX_GUESSES,
    wordLength: WORDLE_WORD_LENGTH,
  };
}

function physicalToKey(eventKey: string): string | null {
  if (eventKey === "Enter") return "ENTER";
  if (eventKey === "Backspace") return "BACKSPACE";
  const upper = eventKey.toLocaleUpperCase("pl-PL");
  return isWordleLetter(upper) ? upper : null;
}

export function WordlePage() {
  const { user } = useSession();
  const { push } = useFlash();
  const [mode, setMode] = useState<WordleMode>(loadMode);
  const [daily, setDaily] = useState<WordlePublicRound | null>(null);
  const [unlimited, setUnlimited] = useState<WordlePublicRound | null>(null);
  const [leaderboard, setLeaderboard] = useState<WordleLeaderboard | null>(null);
  const [allowed, setAllowed] = useState<ReadonlySet<string> | null>(null);
  const [current, setCurrent] = useState("");
  const [stats, setStats] = useState<WordleLocalStats>(loadStats);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [flipRow, setFlipRow] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [scoresOpen, setScoresOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const recordedRef = useRef<Set<string>>(new Set());
  const currentRef = useRef("");
  const dialogOpen = helpOpen || statsOpen || scoresOpen;

  const round = mode === "daily" ? daily : unlimited;

  function setDraft(next: string | ((prev: string) => string)) {
    setCurrent((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      currentRef.current = value;
      return value;
    });
  }

  const syncReset = useCallback((version: number) => {
    if (version <= loadResetVersion()) return;
    const fresh = emptyWordleStats();
    setStats(fresh);
    saveStats(fresh);
    saveResetVersion(version);
    recordedRef.current = new Set();
    push("Admin zresetował postęp. Start od nowa.", "info");
  }, [push]);

  const applyState = useCallback(
    (payload: WordleStateResponse) => {
      syncReset(payload.leaderboard.resetVersion);
      setDaily(payload.daily);
      setUnlimited(payload.unlimited);
      setLeaderboard(payload.leaderboard);
    },
    [syncReset],
  );

  const reload = useCallback(async () => {
    try {
      const payload = await apiRequest<WordleStateResponse>(API_ROUTES.wordle.state);
      applyState(payload);
      setLoadError(null);
    } catch (cause) {
      if (cause instanceof Error && cause.name === "AbortError") return;
      setLoadError(errorMessage(cause));
    }
  }, [applyState]);

  useEffect(() => {
    void reload();
    void apiRequest<WordleAllowedResponse>(API_ROUTES.wordle.allowed)
      .then((payload) => setAllowed(new Set(payload.words)))
      .catch(() => {
        // Server still validates; the local set is only for instant "unknown word".
      });
  }, [reload]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void apiRequest<WordleLeaderboard>(API_ROUTES.wordle.leaderboard)
        .then((board) => {
          syncReset(board.resetVersion);
          setLeaderboard(board);
        })
        .catch(() => {
          // Keep the last board.
        });
    }, LEADERBOARD_MS);
    return () => window.clearInterval(id);
  }, [syncReset]);

  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const keys = useMemo(
    () => (round ? wordleKeyboardColors(round.guesses, round.evaluations) : {}),
    [round],
  );

  const reject = useCallback(
    (message: string) => {
      push(message, "error");
      setShake(true);
      window.setTimeout(() => setShake(false), 500);
    },
    [push],
  );

  const submitGuess = useCallback(async () => {
    if (!round || busy) return;
    if (round.finished) {
      reject("Gra zakończona");
      return;
    }
    const typed = currentRef.current;
    if (typed.length < WORDLE_WORD_LENGTH) {
      reject("Za mało liter");
      return;
    }
    const guess = normalizeWordleGuess(typed);
    if (allowed && !allowed.has(guess)) {
      reject("Nieznane słowo");
      return;
    }

    setBusy(true);
    try {
      const payload = await apiRequest<WordleGuessResponse>(API_ROUTES.wordle.guess, {
        method: "POST",
        body: { mode, guess },
      });
      syncReset(payload.leaderboard.resetVersion);
      setLeaderboard(payload.leaderboard);
      setFlipRow(payload.round.guesses.length - 1);
      if (payload.round.mode === "daily") setDaily(payload.round);
      else setUnlimited(payload.round);
      setDraft("");
      if (payload.round.finished && !round.finished) {
        const key = `${payload.round.mode}:${payload.round.id}`;
        if (!recordedRef.current.has(key)) {
          recordedRef.current.add(key);
          const next = recordWordleFinish(stats, payload.round.won, payload.round.guesses.length);
          setStats(next);
          saveStats(next);
        }
      }
      if (payload.round.won) push("Wygrana!", "success");
      else if (payload.round.finished) push(`Hasło: ${payload.round.answer ?? "?"}`, "info");
    } catch (cause) {
      const message = errorMessage(cause);
      reject(message);
    } finally {
      setBusy(false);
    }
  }, [allowed, busy, mode, push, reject, round, stats, syncReset]);

  const handleInput = useCallback(
    (key: string) => {
      if (!round || round.finished || busy || dialogOpen) return;
      if (key === "ENTER") {
        void submitGuess();
        return;
      }
      if (key === "BACKSPACE") {
        setDraft((value) => value.slice(0, -1));
        return;
      }
      if (key.length === 1) {
        setDraft((value) => (value.length < WORDLE_WORD_LENGTH ? value + key : value));
      }
    },
    [busy, dialogOpen, round, submitGuess],
  );

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      }
      const key = physicalToKey(event.key);
      if (!key) return;
      event.preventDefault();
      handleInput(key);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleInput]);

  async function newUnlimited() {
    if (busy) return;
    setBusy(true);
    try {
      const payload = await apiRequest<WordleGuessResponse>(API_ROUTES.wordle.unlimited, {
        method: "POST",
      });
      setUnlimited(payload.round);
      setLeaderboard(payload.leaderboard);
      setDraft("");
      setFlipRow(null);
      setMode("unlimited");
    } catch (cause) {
      push(errorMessage(cause), "error");
    } finally {
      setBusy(false);
    }
  }

  async function adminReset() {
    if (!confirmAdmin("Zresetować wszystkie wyniki Wordle (dzienne i unlimited)?")) return;
    setBusy(true);
    try {
      const payload = await apiRequest<WordleAdminResetResponse>(API_ROUTES.admin.wordleReset, {
        method: "POST",
      });
      syncReset(payload.resetVersion);
      await reload();
      push("Wyniki wszystkich graczy zostały zresetowane.", "success");
    } catch (cause) {
      push(errorMessage(cause), "error");
    } finally {
      setBusy(false);
    }
  }

  const winRate = stats.played ? Math.round((stats.won / stats.played) * 100) : 0;

  return (
    <div className="wordle">
      <section className="wordle__play">
        <header className="wordle__header">
          <div className="wordle__title-row">
            <h2 className="wordle__title">
              <i className="z-icon" aria-hidden="true">
                🟩
              </i>
              Polskie Wordle
            </h2>
            <div className="wordle__tools">
              <button
                type="button"
                className="wordle__icon-btn"
                aria-label="Pomoc"
                onClick={() => setHelpOpen(true)}
              >
                ?
              </button>
              <button
                type="button"
                className="wordle__icon-btn"
                aria-label="Statystyki"
                onClick={() => setStatsOpen(true)}
              >
                📊
              </button>
              <Button
                size="sm"
                className="wordle-mobile-scores"
                onClick={() => setScoresOpen(true)}
              >
                Wyniki
              </Button>
            </div>
          </div>
          <div className="wordle__modes" aria-label="Tryb gry">
            <Button
              variant={mode === "daily" ? "primary" : "default"}
              onClick={() => {
                setMode("daily");
                setDraft("");
              }}
            >
              Daily
            </Button>
            <Button
              variant={mode === "unlimited" ? "primary" : "default"}
              onClick={() => {
                setMode("unlimited");
                setDraft("");
              }}
            >
              Unlimited
            </Button>
            {mode === "unlimited" ? (
              <Button variant="ghost" disabled={busy} onClick={() => void newUnlimited()}>
                Nowe hasło
              </Button>
            ) : null}
          </div>
          <p className="wordle__status" aria-live="polite">
            {round ? statusText(round) : (loadError ?? "Wczytywanie...")}
          </p>
        </header>

        <Board
          round={round ?? emptyRound(mode)}
          current={current}
          shake={shake}
          flipRow={flipRow}
        />
        <Keyboard colors={keys} disabled={busy || Boolean(round?.finished)} onKey={handleInput} />
      </section>

      <aside className="wordle__side">
        <LeaderboardCard
          title="Daily streak"
          type="daily"
          entries={leaderboard?.dailyStreakEntries ?? []}
        />
        <LeaderboardCard
          title="Unlimited (wynik + passa)"
          type="unlimited"
          entries={leaderboard?.unlimitedBestEntries ?? []}
        />
      </aside>

      {helpOpen ? (
        <div className="wordle-overlay" role="dialog" aria-modal="true" aria-labelledby="wordle-help-title">
          <div className="wordle-overlay__panel">
            <h2 id="wordle-help-title">Jak grać</h2>
            <p>Odgadnij 5-literowe słowo w 6 próbach.</p>
            <ul>
              <li>Zielony: litera jest na właściwym miejscu.</li>
              <li>Żółty: litera jest w słowie, ale na innym miejscu.</li>
              <li>Szary: litery nie ma w haśle.</li>
            </ul>
            <p>Wymagane są poprawne polskie znaki.</p>
            <Button variant="primary" onClick={() => setHelpOpen(false)}>
              Zamknij
            </Button>
          </div>
        </div>
      ) : null}

      {statsOpen ? (
        <div className="wordle-overlay" role="dialog" aria-modal="true" aria-labelledby="wordle-stats-title">
          <div className="wordle-overlay__panel">
            <h2 id="wordle-stats-title">Statystyki</h2>
            <p>
              Rozegrane: <strong>{stats.played}</strong>
            </p>
            <p>
              Wygrane: <strong>{stats.won}</strong> ({winRate}%)
            </p>
            <p>
              Obecna passa: <strong>{stats.currentStreak}</strong>
            </p>
            <p>
              Najlepsza passa: <strong>{stats.maxStreak}</strong>
            </p>
            <p>
              Rozkład wygranych (1-6): <strong>{stats.guessDistribution.join(", ")}</strong>
            </p>
            {!user ? (
              <p className="hub-hint">Zaloguj się u góry, żeby wejść na ranking.</p>
            ) : null}
            {user?.role === "admin" ? (
              <div className="wordle-admin">
                <h3>Panel admina</h3>
                <p>Resetuje wszystkie wyniki leaderboardów Wordle (daily i unlimited).</p>
                <Button type="button" variant="danger" disabled={busy} onClick={() => void adminReset()}>
                  Resetuj wszystkie wyniki
                </Button>
              </div>
            ) : null}
            <Button variant="primary" onClick={() => setStatsOpen(false)}>
              Zamknij
            </Button>
          </div>
        </div>
      ) : null}

      {scoresOpen ? (
        <div
          className="wordle-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wordle-scores-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) setScoresOpen(false);
          }}
        >
          <div className="wordle-overlay__panel">
            <div className="wordle-overlay__header">
              <h2 id="wordle-scores-title">Tablica wyników</h2>
              <button type="button" className="wordle__icon-btn" aria-label="Zamknij" onClick={() => setScoresOpen(false)}>
                ×
              </button>
            </div>
            <LeaderboardCard
              title="Daily streak"
              type="daily"
              entries={leaderboard?.dailyStreakEntries ?? []}
            />
            <LeaderboardCard
              title="Unlimited (wynik + passa)"
              type="unlimited"
              entries={leaderboard?.unlimitedBestEntries ?? []}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Board({
  round,
  current,
  shake,
  flipRow,
}: {
  round: WordlePublicRound;
  current: string;
  shake: boolean;
  flipRow: number | null;
}) {
  const rows = [];
  for (let row = 0; row < WORDLE_MAX_GUESSES; row += 1) {
    const guess = round.guesses[row] ?? "";
    const evals = round.evaluations[row] ?? [];
    const isCurrent = row === round.guesses.length;
    const letters = isCurrent ? current : guess;
    const shaking = isCurrent && shake;
    rows.push(
      <div className="wordle-row" key={row}>
        {Array.from({ length: WORDLE_WORD_LENGTH }, (_, col) => {
          const ch = letters[col] ?? "";
          const color = evals[col];
          const classes = ["wordle-tile"];
          if (ch) classes.push("wordle-tile--filled");
          if (color) classes.push(`wordle-tile--${color}`);
          if (shaking) classes.push("wordle-tile--shake");
          if (flipRow === row && color) classes.push("wordle-tile--flip");
          return (
            <div
              className={classes.join(" ")}
              key={col}
              style={flipRow === row && color ? { animationDelay: `${col * 120}ms` } : undefined}
            >
              {ch}
            </div>
          );
        })}
      </div>,
    );
  }
  return (
    <section className="wordle__board" aria-label="Plansza Wordle">
      {rows}
    </section>
  );
}

function Keyboard({
  colors,
  disabled,
  onKey,
}: {
  colors: Record<string, WordleColor>;
  disabled: boolean;
  onKey: (key: string) => void;
}) {
  return (
    <section className="wordle__keyboard" aria-label="Klawiatura ekranowa">
      {WORDLE_KEY_ROWS.map((row) => (
        <div className="wordle-key-row" key={row.join("")}>
          {row.map((key) => {
            const color = colors[key];
            const classes = ["wordle-key"];
            if (key === "ENTER" || key === "BACKSPACE") classes.push("wordle-key--wide");
            if (color) classes.push(`wordle-key--${color}`);
            return (
              <button
                key={key}
                type="button"
                className={classes.join(" ")}
                disabled={disabled}
                onClick={() => onKey(key)}
              >
                {key === "BACKSPACE" ? "⌫" : key}
              </button>
            );
          })}
        </div>
      ))}
    </section>
  );
}

function LeaderboardCard({
  title,
  type,
  entries,
}: {
  title: string;
  type: "daily" | "unlimited";
  entries: WordleLeaderboard["dailyStreakEntries"] | WordleLeaderboard["unlimitedBestEntries"];
}) {
  return (
    <Card variant="panel" title={title} icon="⭐">
      {entries.length === 0 ? (
        <p className="hub-hint">Brak wyników.</p>
      ) : (
        <div className="wordle-board-list">
          {entries.map((entry, index) => (
            <div
              key={`${entry.username}-${index}`}
              className={`wordle-board-row${type === "unlimited" ? " wordle-board-row--unlimited" : ""}`}
              data-rank={index + 1}
            >
              <span>{index + 1}</span>
              <span className="wordle-board-row__name">{entry.username}</span>
              {"bestGuesses" in entry ? (
                <span className="wordle-board-row__metric">{entry.bestGuesses}/6</span>
              ) : null}
              <span className="wordle-board-row__metric">Akt: {entry.currentStreak}</span>
              <span className="wordle-board-row__metric">Max: {entry.maxStreak}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
